import React from 'react';
import { useBillingSummary } from '../../hooks/useBilling';
import { InvoicesSection } from './invoices/InvoicesSection';
import { KpiCards } from './KpiCards';
import { KpiStatSkeleton } from './shared/Card';

const KPI_SKELETON_COUNT = 5;

// KPIs are ALL TIME (both bounds omitted). A trailing window couldn't
// reconcile: outstanding has never been windowed, so billed and paid counted
// a year against a balance counting everything.

// Single-view billing page — KPI recap, then the invoices table with its own
// search/filter/pagination/drawer. The Overview/Invoices tabs, charts
// (monthly, margin donut, open-invoices recap), attention banner, and status
// filter cards from earlier iterations were all removed; see git history.
//
// Viewport-locked (no page scroll): rendered inside `<SidebarLayout noScroll>`
// (Routes/billing.tsx), which gives `main` a definite, non-scrolling height
// and makes its content wrapper `flex-1 min-h-0 flex flex-col` — this root
// div fills that via `h-full flex flex-col min-h-0`, header/KPI row stay
// `shrink-0`, and InvoicesSection claims the rest with `flex-1 min-h-0` so
// its own table area can compute exactly how many rows fit.
const BillingPage: React.FC = () => {
    const {
        data: summary,
        isLoading: isLoadingSummary,
        isFetching: isFetchingSummary
    } = useBillingSummary(null, null);

    return (
        <div className="h-full flex flex-col min-h-0">
            {/* Header */}
            <div className="px-8 pt-8 shrink-0">
                <h2 className="text-2xl font-bold text-gray-800">Billing</h2>
            </div>

            {/* KPI recap */}
            <div className="px-8 mt-5 shrink-0">
                {summary ? (
                    <KpiCards kpis={summary.kpis} />
                ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        {Array.from({ length: KPI_SKELETON_COUNT }).map((_, index) => (
                            <KpiStatSkeleton key={index} />
                        ))}
                    </div>
                )}
                {isFetchingSummary && !isLoadingSummary && (
                    <p className="text-xs text-gray-400 mt-2">Refreshing…</p>
                )}
            </div>

            {/* Invoices: toolbar + table + drawer — claims the remaining height */}
            <div className="px-8 pb-8 mt-5 flex-1 min-h-0 flex flex-col">
                <InvoicesSection />
            </div>
        </div>
    );
};

export default BillingPage;
